/**
 * The support request detail and its workflow — Task 13.3, requirements 9.3, 9.6–9.7, 10.1, and 15.1–15.5.
 *
 * ## Controls are rendered from the role matrix, not hidden after the fact
 *
 * Every control here is present only when the session may actually perform it. That is the requirement —
 * perform no request behind a forbidden control — and it is also the difference between a dashboard that
 * teaches its own rules and one that offers a button and answers 403. `permissions.ts` mirrors the server's
 * transition table for this; the server re-checks all of it and remains the authority.
 *
 * ## No reload, ever
 *
 * A mutation applies exactly what the API returned and then reconciles in the background, which
 * `useManagedSupportRequest` owns. Nothing here calls `location.reload` or refetches the route: a queue that
 * lost its filters every time someone claimed a request is a queue nobody works through.
 *
 * ## A conflict is a real answer, not a fault
 *
 * Two people working one queue will collide, and the API says so with 409 — a status that moved under you, an
 * assignee that changed, or a request that has since been redacted. That is reported as a stale-state message
 * with the current state offered, rather than as an error, because nothing went wrong and the remedy is to look
 * again.
 *
 * ## Assignment without a staff directory
 *
 * The API accepts any active staff id and validates it, but no endpoint lists staff — `staff-directory.ts`
 * resolves one id at a time to check a write. So claiming for yourself and releasing are ordinary buttons, and
 * assigning to somebody else is an id field for manager and admin. That is honest about what exists rather than
 * inventing a picker over data the client cannot obtain; a named picker needs a directory endpoint, which is a
 * server change and not this task. `docs/open-items.md` carries it.
 */
import type {
  StaffRole,
  SupportRequestDetail,
  SupportRequestEvent,
  SupportRequestNote,
  SupportRequestStatus,
  SupportSideEffectOutcome,
  SupportTriageOutcome,
} from '@apuestapr/contracts';
import { supportContractLimits } from '@apuestapr/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Textarea,
  toast,
} from '@apuestapr/ui';
import { useId, useState, type FormEvent, type ReactElement } from 'react';

import type { ApiFailure } from '../api/client.ts';
import { formatPuertoRicoInstant, puertoRicoZoneLabel } from '../format/instants.ts';
import type { ManagedSupportRequest } from './hooks.ts';
import { allowedSupportStatusChanges, canSupport } from './permissions.ts';
import { RegistrationCandidates } from './RegistrationCandidates.tsx';
import { RetentionPanel } from './RetentionPanel.tsx';
import {
  supportCategoryLabels,
  supportSideEffectLabels,
  supportStatusBadge,
  supportStatusLabels,
} from './vocabulary.ts';

const Instant = ({ instant }: { readonly instant: string }): ReactElement => (
  <span className="grid gap-1">
    <time dateTime={instant}>{formatPuertoRicoInstant(instant)}</time>
    <span className="text-xs text-muted-foreground">{puertoRicoZoneLabel}</span>
  </span>
);

const StatusBadge = ({ status }: { readonly status: SupportRequestStatus }): ReactElement => {
  const { variant, shape } = supportStatusBadge[status];
  return (
    <Badge variant={variant} shape={shape}>
      {supportStatusLabels[status]}
    </Badge>
  );
};

/** True when the request has been redacted, which makes every mutation a conflict on the server. */
const isRedacted = (detail: SupportRequestDetail): boolean => detail.redactedAt !== null;

const SubmittedDetail = ({ detail }: { readonly detail: SupportRequestDetail }): ReactElement => (
  <Card>
    <CardHeader>
      <CardTitle level={3}>Datos de la solicitud</CardTitle>
    </CardHeader>
    <CardContent>
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="font-semibold">Cliente</dt>
          <dd>{detail.customerName ?? 'Sin nombre'}</dd>
        </div>
        <div>
          <dt className="font-semibold">Correo electrónico</dt>
          {/* Redaction replaces the value with null, so an absent value is stated rather than blank. */}
          <dd>{detail.email ?? 'No disponible'}</dd>
        </div>
        <div>
          <dt className="font-semibold">Categoría</dt>
          <dd>{supportCategoryLabels[detail.category]}</dd>
        </div>
        <div>
          <dt className="font-semibold">Estado</dt>
          <dd>
            <StatusBadge status={detail.status} />
          </dd>
        </div>
        <div>
          <dt className="font-semibold">Creada</dt>
          <dd>
            <Instant instant={detail.createdAt} />
          </dd>
        </div>
        <div>
          <dt className="font-semibold">Actualizada</dt>
          <dd>
            <Instant instant={detail.updatedAt} />
          </dd>
        </div>
        <div>
          <dt className="font-semibold">Asignada a</dt>
          <dd>{detail.assignedTo?.name ?? 'Sin asignar'}</dd>
        </div>
        <div>
          <dt className="font-semibold">Resuelta</dt>
          <dd>
            {detail.resolvedAt === null ? 'Sin resolver' : <Instant instant={detail.resolvedAt} />}
          </dd>
        </div>
      </dl>
      <h4 className="mb-1 mt-4 font-semibold">Mensaje</h4>
      <p className="m-0 whitespace-pre-wrap">{detail.message ?? 'No disponible'}</p>
    </CardContent>
  </Card>
);

interface StatusControlsProps {
  readonly detail: SupportRequestDetail;
  readonly role: StaffRole;
  readonly busy: boolean;
  readonly onChange: (status: SupportRequestStatus) => void;
}

const StatusControls = ({ detail, role, busy, onChange }: StatusControlsProps): ReactElement => {
  const allowed = allowedSupportStatusChanges(detail.status, role);

  return (
    <Card>
      <CardHeader>
        <CardTitle level={3}>Estado</CardTitle>
      </CardHeader>
      <CardContent>
        {allowed.length === 0 ? (
          <p className="m-0">No hay cambios de estado disponibles para tu rol.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {allowed.map((status) => (
              <Button
                key={status}
                size="sm"
                variant={status === 'spam' ? 'destructive' : 'outline'}
                disabled={busy}
                onClick={() => onChange(status)}
              >
                {supportStatusLabels[status]}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface AssignmentControlsProps {
  readonly detail: SupportRequestDetail;
  readonly role: StaffRole;
  readonly selfId: number;
  readonly busy: boolean;
  readonly onAssign: (assigneeId: number | null) => void;
}

const AssignmentControls = ({
  detail,
  role,
  selfId,
  busy,
  onAssign,
}: AssignmentControlsProps): ReactElement => {
  const [target, setTarget] = useState('');
  const assignedId = detail.assignedTo?.id ?? null;
  const mine = assignedId === selfId;
  const canAssignAny = canSupport(role, 'assignAny');
  // An agente may release only its own request; a manager may release anybody's.
  const canRelease = assignedId !== null && (canAssignAny || mine);

  const submitTarget = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = Number(target.trim());
    if (!Number.isInteger(parsed) || parsed <= 0) return;
    onAssign(parsed);
    setTarget('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle level={3}>Asignación</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {mine ? null : (
            <Button size="sm" disabled={busy} onClick={() => onAssign(selfId)}>
              Asignármela
            </Button>
          )}
          {canRelease ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onAssign(null)}>
              Liberar
            </Button>
          ) : null}
        </div>

        {canAssignAny ? (
          <form className="space-y-2" noValidate onSubmit={submitTarget}>
            <Field
              label="Asignar a otra persona (identificador de usuario)"
              description="No existe un directorio de personal en la API, así que se indica el identificador. El servidor verifica que sea un usuario de soporte activo."
            >
              {(control) => (
                <Input
                  {...control}
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  disabled={busy}
                  value={target}
                  onChange={(event) => setTarget(event.target.value)}
                />
              )}
            </Field>
            <Button
              size="sm"
              type="submit"
              variant="outline"
              disabled={busy || target.trim() === ''}
            >
              Asignar
            </Button>
          </form>
        ) : null}
      </CardContent>
    </Card>
  );
};

interface NotesProps {
  readonly notes: readonly SupportRequestNote[];
  readonly busy: boolean;
  readonly onAdd: (body: string) => void;
}

/**
 * Notes are append-only, and the form says so.
 *
 * The API offers no edit and no delete, which is the point of an internal record: what somebody wrote at the
 * time is what the next person reads. Nothing here pretends otherwise.
 */
const Notes = ({ notes, busy, onAdd }: NotesProps): ReactElement => {
  const [body, setBody] = useState('');
  const headingId = useId();

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = body.trim();
    if (trimmed === '') return;
    onAdd(trimmed);
    setBody('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle level={3} id={headingId}>
          Notas internas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {notes.length === 0 ? (
          <p className="m-0">Sin notas.</p>
        ) : (
          <ol className="m-0 grid list-none gap-3 p-0">
            {notes.map((note) => (
              <li
                key={`${note.createdAt}:${note.author.id}`}
                className="rounded-md border border-border p-3"
              >
                <p className="m-0 whitespace-pre-wrap">{note.body}</p>
                <p className="m-0 mt-2 text-sm text-muted-foreground">
                  {note.author.name} · <Instant instant={note.createdAt} />
                </p>
              </li>
            ))}
          </ol>
        )}

        <form className="space-y-2" noValidate onSubmit={submit}>
          <Field label="Añadir una nota" description="Las notas no se pueden editar ni eliminar.">
            {(control) => (
              <Textarea
                {...control}
                rows={3}
                maxLength={supportContractLimits.note}
                disabled={busy}
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />
            )}
          </Field>
          <Button size="sm" type="submit" disabled={busy || body.trim() === ''}>
            Guardar nota
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const eventLabels: Readonly<Record<SupportRequestEvent['type'], string>> = {
  created: 'Creada',
  status_changed: 'Cambio de estado',
  assignment_changed: 'Cambio de asignación',
  note_added: 'Nota añadida',
  retention_hold_changed: 'Retención actualizada',
  triage_outcome: 'Resultado de clasificación',
  notification_outcome: 'Resultado de notificación',
  redacted: 'Datos redactados',
};

const EventHistory = ({
  events,
}: {
  readonly events: readonly SupportRequestEvent[];
}): ReactElement => (
  <Card>
    <CardHeader>
      <CardTitle level={3}>Historial</CardTitle>
    </CardHeader>
    <CardContent>
      {events.length === 0 ? (
        <p className="m-0">Sin eventos.</p>
      ) : (
        <ol className="m-0 grid list-none gap-3 p-0">
          {/* Server order, oldest first. */}
          {events.map((event, index) => (
            <li
              key={`${event.createdAt}:${String(index)}`}
              className="rounded-md border border-border p-3"
            >
              <p className="m-0 font-semibold">{eventLabels[event.type]}</p>
              {event.from === null && event.to === null ? null : (
                <p className="m-0 text-sm">
                  {event.from ?? 'sin valor'} → {event.to ?? 'sin valor'}
                </p>
              )}
              {event.detail === null ? null : <p className="m-0 text-sm">{event.detail}</p>}
              <p className="m-0 mt-2 text-sm text-muted-foreground">
                {event.actor?.name ?? 'Sistema'} · <Instant instant={event.createdAt} />
              </p>
            </li>
          ))}
        </ol>
      )}
    </CardContent>
  </Card>
);

/**
 * One side effect, with its interruption stated separately from its status — Task 13.4.
 *
 * `interrupted` is derived, not stored: the API sets it when a side effect has been `pending` for longer than
 * five minutes, which means the process that was going to finish it did not. That is materially different from
 * `failed`, where something tried and got an answer, and from `pending`, where it may still be in flight. It is
 * shown as its own cue because "pending" on its own reads as fine, and this is the case where it is not.
 *
 * There is no retry control, deliberately. No notification provider has been selected, so a retry button would
 * either do nothing or imply a delivery guarantee that does not exist. The durable queue is the guarantee.
 */
const SideEffect = ({
  label,
  outcome,
}: {
  readonly label: string;
  readonly outcome: SupportSideEffectOutcome | SupportTriageOutcome;
}): ReactElement => (
  <div className="rounded-md border border-border p-3">
    <p className="m-0 font-semibold">{label}</p>
    <p className="m-0 flex flex-wrap items-center gap-2">
      {supportSideEffectLabels[outcome.status]}
      {outcome.interrupted ? (
        <Badge variant="destructive" shape="diamond">
          Interrumpido
        </Badge>
      ) : null}
    </p>
    {outcome.interrupted ? (
      <p className="m-0 mt-1 text-sm">
        Quedó pendiente más de cinco minutos, así que el proceso que debía completarlo no terminó.
        No se registró como completado.
      </p>
    ) : null}
    {outcome.message === null ? null : <p className="m-0 text-sm">{outcome.message}</p>}
    {outcome.updatedAt === null ? null : (
      <p className="m-0 mt-2 text-sm text-muted-foreground">
        <Instant instant={outcome.updatedAt} />
      </p>
    )}
  </div>
);

const SideEffects = ({ detail }: { readonly detail: SupportRequestDetail }): ReactElement => (
  <Card>
    <CardHeader>
      <CardTitle level={3}>Efectos secundarios</CardTitle>
    </CardHeader>
    <CardContent className="grid gap-3 sm:grid-cols-2">
      <SideEffect label="Clasificación" outcome={detail.triage} />
      <SideEffect label="Notificación" outcome={detail.notification} />
    </CardContent>
  </Card>
);

export interface SupportRequestViewProps {
  readonly detail: SupportRequestDetail;
  readonly role: StaffRole;
  readonly selfId: number;
  readonly managed: ManagedSupportRequest;
  /** Injected so the retention panel's 90-day ceiling is deterministic in a test. */
  readonly now?: Date | undefined;
}

/**
 * A conflict means the request moved under you. It is reported with the remedy — look again — rather than as a
 * failure, because nothing is broken and a retry of the same intent may well be right afterwards.
 */
const isConflict = (failure: ApiFailure): boolean =>
  failure.kind === 'http' && failure.status === 409;

export const SupportRequestView = ({
  detail,
  role,
  selfId,
  managed,
  now,
}: SupportRequestViewProps): ReactElement => {
  const busy = managed.mutation.status === 'loading';
  const redacted = isRedacted(detail);
  const failure = managed.mutation.status === 'error' ? managed.mutation : null;

  return (
    <div className="grid gap-4">
      {redacted ? (
        <div className="rounded-xl border-2 border-foreground bg-card p-4" role="status">
          <p className="m-0 font-semibold">Esta solicitud fue redactada por retención de datos.</p>
          <p className="m-0">
            Los datos de la persona ya no están disponibles y no se pueden registrar cambios.
          </p>
        </div>
      ) : null}

      {failure === null ? null : (
        <div className="rounded-xl border-2 border-foreground bg-card p-4" role="alert">
          <p className="m-0">
            {isConflict(failure.failure)
              ? 'La solicitud cambió mientras la tenías abierta. Vuelve a cargarla antes de intentarlo de nuevo.'
              : failure.access === 'forbidden'
                ? 'Tu rol no tiene permiso para esa operación.'
                : 'No se pudo registrar el cambio. Inténtalo de nuevo.'}
          </p>
          <Button
            className="mt-3"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void managed.reconcile()}
          >
            Volver a cargar
          </Button>
        </div>
      )}

      {managed.reconciling ? (
        <p className="sr-only" role="status">
          Actualizando la solicitud…
        </p>
      ) : null}

      <SubmittedDetail detail={detail} />

      {redacted ? null : (
        <>
          <StatusControls
            detail={detail}
            role={role}
            busy={busy}
            onChange={(status) =>
              void managed.changeStatus({ status }).then((result) => {
                if (result.ok) toast({ title: `Estado cambiado a ${supportStatusLabels[status]}` });
              })
            }
          />
          <AssignmentControls
            detail={detail}
            role={role}
            selfId={selfId}
            busy={busy}
            onAssign={(assigneeId) =>
              void managed.changeAssignment({ assigneeId }).then((result) => {
                if (result.ok) {
                  toast({
                    title: assigneeId === null ? 'Solicitud liberada' : 'Solicitud asignada',
                  });
                }
              })
            }
          />
        </>
      )}

      <Notes
        notes={detail.notes}
        busy={busy || redacted}
        onAdd={(body) =>
          void managed.addNote({ body }).then((result) => {
            if (result.ok) toast({ title: 'Nota guardada' });
          })
        }
      />
      <RegistrationCandidates detail={detail} />
      <RetentionPanel
        detail={detail}
        role={role}
        busy={busy}
        onRecord={(until, reason) =>
          void managed.changeRetentionHold({ until, reason }).then((result) => {
            if (result.ok) toast({ title: 'Retención registrada' });
          })
        }
        onLift={() =>
          void managed.changeRetentionHold({ until: null, reason: null }).then((result) => {
            if (result.ok) toast({ title: 'Retención levantada' });
          })
        }
        {...(now === undefined ? {} : { now })}
      />
      <SideEffects detail={detail} />
      <EventHistory events={detail.events} />
    </div>
  );
};
